import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  useChatSocketClient,
  useMetaDataClient,
  AddToCart,
  MAX_PRODUCTS_IN_CART,
} from '@nizza/core';
import { useSessionId } from './useSessionId';

type Props = {
  eventId: string;
};

export const useRealTimeNotification = ({ eventId }: Props) => {
  const { sessionId } = useSessionId();
  const metaData = useMetaDataClient();
  const userName = useMemo(() => uuidv4(), []);
  const socket = useChatSocketClient({ eventId, userName });

  const [productsInCart, setProductsInCart] = useState<AddToCart[]>([]);
  const [quantityProductsInCartShown, setQuantityProductsInCartShown] =
    useState(0);

  const addProductsInCart = (products: AddToCart[]) => {
    setProductsInCart(prevProducts => {
      const allProducts = [...prevProducts, ...products];

      const uniqueProducts = Array.from(
        new Map(allProducts.map(product => [product.id, product])).values(),
      );

      if (uniqueProducts.length > MAX_PRODUCTS_IN_CART) return prevProducts;

      return uniqueProducts;
    });
  };

  useEffect(() => {
    const products: AddToCart[] = [
      ...socket.productsInCart,
      ...metaData.productsInCart,
    ];

    const filterProducts = products.filter(
      product => sessionId !== product.sessionId,
    );

    addProductsInCart(filterProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaData.productsInCart, socket.productsInCart]);

  useEffect(() => {
    if (quantityProductsInCartShown < MAX_PRODUCTS_IN_CART) return;

    setProductsInCart([]);
    socket.setProductsInCart([]);
    metaData.setProductsInCart([]);
    setQuantityProductsInCartShown(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityProductsInCartShown]);

  return {
    socket: {
      ...socket,
    },
    metaData: {
      ...metaData,
    },
    shared: {
      productsInCart,
      addProductsInCart,
      setQuantityProductsInCartShown,
    },
  };
};
